// Registro de los formularios discretos del Módulo H, Grupo 4 — códigos que
// NO usan el motor de Questionnaire: construyen/editan su propio recurso
// FHIR directamente (Observation, MedicationRequest/Dispense/Administration,
// ServiceRequest/DiagnosticReport, Composition, Procedure, Condition...).
// Puerto de packages/hce/src/hce/forms/index.ts (FORMULARIOS_DISCRETOS) a
// shadcn/Tailwind, sin Mantine.
//
// Diferencia clave con el original: cada componente ya no recibe
// `onSubmitted: () => void` sino `onGuardado: (pares: Par[]) => void` — el
// punto 11 del pedido ("todo documento debe llevar firma e impresión")
// exige que FormularioDiscretoPage arme un PDF (modo `pares`,
// pdfFormularioGenerico.ts) y ofrezca PanelFirmaImpresion después de cada
// guardado, así que cada formulario reporta los pares pregunta/respuesta que
// acaba de guardar en vez de solo avisar "listo".
import type { Encounter, Patient, Reference } from '@medplum/fhirtypes';
import type { ComponentType } from 'react';
import type { DatosCurvasCrecimientoPdf, DatosDenverPdf, DatosEncabezadoMsp, DatosGraficoConstantesUciPdf, DatosOdontogramaPdf, DatosPartogramaPdf, DatosSignosVitalesPdf, DatosSiluetaPdf } from '@logiacare/hce-pdf';
import { Formulario005 } from './Formulario005';
import { Formulario006 } from './Formulario006';
import { Formulario007 } from './Formulario007';
import { Formulario008 } from './Formulario008';
import { Formulario010A } from './Formulario010A';
import { Formulario010B } from './Formulario010B';
import { Formulario012A } from './Formulario012A';
import { Formulario012B } from './Formulario012B';
import { Formulario013A } from './Formulario013A';
import { Formulario013B } from './Formulario013B';
import { Formulario016 } from './Formulario016';
import { Formulario016A } from './Formulario016A';
import { Formulario016B } from './Formulario016B';
import { Formulario016C } from './Formulario016C';
import { Formulario058 } from './Formulario058';
import { Formulario058A } from './Formulario058A';
import { Formulario002 } from './Formulario002';
import { Formulario003 } from './Formulario003';
import { Formulario056 } from './Formulario056';
import { Formulario056A } from './Formulario056A';
import { Formulario056B } from './Formulario056B';
import { Formulario057 } from './Formulario057';
import { Formulario057A } from './Formulario057A';
import { Formulario060 } from './Formulario060';
import { Formulario121 } from './Formulario121';
import { Formulario054 } from './Formulario054';
import { Formulario055 } from './Formulario055';
import { Formulario017 } from './Formulario017';
import { Formulario018 } from './Formulario018';
import { Formulario018A } from './Formulario018A';
import { Formulario019 } from './Formulario019';
import { Formulario020 } from './Formulario020';
import { Formulario022 } from './Formulario022';
import { Formulario033 } from './Formulario033';
import { Formulario028 } from './Formulario028';
import { Formulario028A } from './Formulario028A';
import { Formulario028B } from './Formulario028B';
import { Formulario028C } from './Formulario028C';
import { Formulario028A1 } from './Formulario028A1';
import { Formulario028B1 } from './Formulario028B1';
import { Formulario028B2 } from './Formulario028B2';
import { Formulario028C1 } from './Formulario028C1';
import { Formulario028C2 } from './Formulario028C2';
import { Formulario034 } from './Formulario034';
import { Formulario038 } from './Formulario038';
import { Formulario051 } from './Formulario051';
import { FormularioAnexo1 } from './FormularioAnexo1';
import { FormularioAnexo2 } from './FormularioAnexo2';
import { Formulario052 } from './Formulario052';
import { Formulario053 } from './Formulario053';
import { Formulario114 } from './Formulario114';
import { Formulario115 } from './Formulario115';
import { Formulario113 } from './Formulario113';
import { Formulario116Certificado } from './Formulario116Certificado';
import { Formulario117 } from './Formulario117';
import { Formulario118 } from './Formulario118';
import { Formulario119 } from './Formulario119';
import { Formulario120 } from './Formulario120';
import { Formulario122 } from './Formulario122';
import { FormularioSoapie } from './FormularioSoapie';

export interface Par {
  pregunta: string;
  respuesta: string;
}

export interface FormularioDiscretoProps {
  readonly subject: Reference<Patient>;
  readonly encounter?: Reference<Encounter>;
  /**
   * `datosEncabezadoMsp` — SOLO los 10 formularios del Anexo 1 MSP (Grupo 4,
   * `useDatosEncabezadoMsp.ts`) lo pasan; el resto de los formularios
   * discretos sigue reportando solo `pares` (`undefined`), y
   * `FormularioDiscretoPage` arma el PDF con el header genérico de siempre
   * (`generarPdfFormularioDePares`) en ese caso.
   */
  /**
   * `relacionado` (2026-09-13) — el recurso que ESTE guardado creó y que el
   * PDF firmado documenta, para que `PanelFirmaImpresion` lo deje en
   * `DocumentReference.context.related` y después se pueda encontrar el PDF
   * desde el recurso (hoy solo lo usa la nota de turno SOAPIE, que se lee
   * desde la pestaña "Nota de turno"). Los formularios que crean VARIOS
   * recursos por guardado (020, 022, 120…) no lo mandan: no hay un único
   * recurso que represente el documento. Puede ser una función: entonces el
   * recurso se crea DESPUÉS de generar y firmar el PDF (la nota SOAPIE), así
   * no queda un recurso sin PDF; debe ser idempotente (se reintenta).
   */
  readonly onGuardado: (
    pares: Par[],
    datosEncabezadoMsp?: DatosEncabezadoMsp,
    relacionado?: Reference | (() => Promise<Reference>),
    diagramaOdontograma?: DatosOdontogramaPdf,
    diagramaDenver?: DatosDenverPdf,
    diagramaSignosVitales?: DatosSignosVitalesPdf,
    /** Solo 051 (Fase 4) — gráfico dilatación×horas + curva de alerta + tabla horaria, para dibujarlo en el PDF firmado. */
    diagramaPartograma?: DatosPartogramaPdf,
    /** Solo 113/114/115 (Fase 4) — gráfico(s) de constantes superpuestas (escala por serie), un elemento por caja impresa. 113 manda 1, 114 manda 2 (temperatura, PA/pulso/FR). */
    diagramasGraficoConstantesUci?: readonly DatosGraficoConstantesUciPdf[],
    /** Solo 003 (Fase 7) — regiones marcadas en la silueta corporal, para dibujarlas en el PDF firmado. */
    siluetaCorporal?: DatosSiluetaPdf,
    /** Solo 056A/056B — las cuatro curvas de crecimiento (referencia + mediciones del paciente), para dibujarlas en el PDF firmado. */
    curvasCrecimiento?: DatosCurvasCrecimientoPdf,
  ) => void;
  /** `Condition` que justifica el `Procedure`/recurso para auditorías de glosa — todavía no hay selector de UI que la capture, queda lista para cuando exista (mismo patrón que packages/hce/src/hce/forms). Solo la usan 016/119/120/122. */
  readonly conditionId?: string;
}

// 001 no está acá: EDITA un Encounter existente (Grupo 3, Formulario001Page.tsx).
export const FORMULARIOS_DISCRETOS: Record<string, ComponentType<FormularioDiscretoProps>> = {
  '002': Formulario002,
  '003': Formulario003,
  '005': Formulario005,
  '006': Formulario006,
  '007': Formulario007,
  '008': Formulario008,
  '020': Formulario020,
  '022': Formulario022,
  '010A': Formulario010A,
  '010B': Formulario010B,
  '012A': Formulario012A,
  '012B': Formulario012B,
  '013A': Formulario013A,
  '013B': Formulario013B,
  '118': Formulario118,
  '122': Formulario122,
  '054': Formulario054,
  '055': Formulario055,
  '117': Formulario117,
  '116-cert': Formulario116Certificado,
  Anexo1: FormularioAnexo1,
  Anexo2: FormularioAnexo2,
  '052': Formulario052,
  '053': Formulario053,
  '058': Formulario058,
  '058A': Formulario058A,
  '056': Formulario056,
  '056A': Formulario056A,
  '056B': Formulario056B,
  '057': Formulario057,
  '057A': Formulario057A,
  '060': Formulario060,
  '121': Formulario121,
  '119': Formulario119,
  '120': Formulario120,
  '028': Formulario028,
  '028A': Formulario028A,
  '028B': Formulario028B,
  '028C': Formulario028C,
  '028A-1': Formulario028A1,
  '028B-1': Formulario028B1,
  '028B-2': Formulario028B2,
  '028C-1': Formulario028C1,
  '028C-2': Formulario028C2,
  '033': Formulario033,
  '034': Formulario034,
  '038': Formulario038,
  '051': Formulario051,
  '017': Formulario017,
  '018': Formulario018,
  '018A': Formulario018A,
  '019': Formulario019,
  '016': Formulario016,
  '016A': Formulario016A,
  '016B': Formulario016B,
  '016C': Formulario016C,
  '113': Formulario113,
  '114': Formulario114,
  '115': Formulario115,
  // Nota de turno de enfermería SOAPIE — código propio, NO es un formulario
  // MSP (no está en el Anexo 1): PDF en modo 'pares', sin encabezado ni
  // leyenda SNS-MSP. Mismo namespace con prefijo que las escalas ("esc-").
  'enf-soapie': FormularioSoapie,
};
